import { useQuery } from "@tanstack/react-query";
import type { ManualSearchResult } from "@arc/contracts";
import type { AuthSession } from "../../auth/keycloak.js";
import { manualApi } from "../../api/client.js";

/**
 * Global manual search (`GET /manuals/search`, FN4b-2 — the legacy "Suche" Handlungsanweisungen/Beschreibung tabs).
 * Permission-filtered server-side to the caller's DIRECTLY-viewable facilities (the #107 predicate — a non-viewable
 * manual is simply absent, never a 403); returns the manual's card fields + its facility location + a plain-text
 * snippet. `field` ("name" | "description") is in the query key so the two tabs cache separately.
 *
 * Gated until there's a session, a non-empty term, AND `enabled` (the caller passes `false` while a non-manual tab is
 * showing — so browsing the Facilities tab fires no request). The term only changes on SUBMIT (Enter / an `arc_search`
 * deep-link), never per keystroke, so no debounce is needed. Sliced to the contract's 200-char cap so an over-long
 * paste can't 400. Errors surface as thrown.
 */
export function useManualSearch(session: AuthSession | null, q: string, field: "name" | "description", enabled = true) {
  const term = q.trim().slice(0, 200);
  return useQuery({
    queryKey: ["manual-search", session?.realm, session?.sub, field, term],
    enabled: enabled && session !== null && term.length > 0,
    queryFn: async (): Promise<ManualSearchResult[]> => {
      const res = await manualApi.searchManuals({ query: { q: term, field } });
      if (res.status !== 200) throw new Error(`GET /manuals/search?field=${field} returned ${res.status}`);
      return res.body.results;
    },
  });
}
